import classNames from 'classnames';
import { Bot, CheckCheck, FolderOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { ConversationSourceIcon } from './ConversationSourceIcon';
import { isPublicAssistantSource, publicAssistantLabel } from './assistantSource';
import { ConversationDto } from '@/Domain/api/Conversation/dto/Conversation.dto';
import { Avatar, AvatarFallback, AvatarImage } from '@/Domain/components/Shared/Avatar/Avatar';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { ConversationAssigneeEnum } from '@/Domain/enum/ConversationAssignee.enum';
import { ConversationStatusEnum } from '@/Domain/enum/ConversationStatus.enum';
import { ConversationHelper } from '@/Domain/helper/Conversation.helper';
import { UserHelper } from '@/Domain/helper/User.helper';
import { formatConversationBadge } from '@/Domain/helper/conversationBadge.helper';
import { formatDateToTimeAgo } from '@/Domain/utils/formatDateToTimeAgo';

type Props = {
  conversation: ConversationDto;
  isActive: boolean;
  onHoverChange: (hovering: boolean) => void;
};

export const ConversationItem = ({ conversation, isActive, onHoverChange }: Props) => {
  const { t } = useTranslation(['inbox_v2', 'sidebar_menu']);
  const [searchParams] = useSearchParams();

  const isUnread = conversation.has_unread_messages;
  const needsAttention = conversation.needs_attention;
  const isUnassigned = conversation.assignee.type === ConversationAssigneeEnum.UNASSIGNED;

  const conversationTitle = conversation.visitor_label || t('un_known_visitor');
  const sourceLabel = isPublicAssistantSource(conversation.source)
    ? publicAssistantLabel(conversation.source, conversation.assistant_source)
    : t(
        `sources.${conversation.source}`,
        ConversationHelper.humanizeConversationSource(conversation.source),
      );
  const assigneeLabel = (() => {
    switch (conversation.assignee.type) {
      case ConversationAssigneeEnum.AI_ASSISTANT:
        return t('assign_filter.options.ai_agent');
      case ConversationAssigneeEnum.HUMAN_OPERATOR:
        return (
          UserHelper.getFullName(conversation.assignee.user) ||
          conversation.assignee.user.email ||
          `${t('tabs.resolved_by_human')} #${conversation.assignee.user.id}`
        );
      default:
        return t('assign_filter.options.unassigned');
    }
  })();

  const getAssigneeInitials = () => {
    if (conversation.assignee.type !== ConversationAssigneeEnum.HUMAN_OPERATOR) {
      return '';
    }

    const firstName = conversation.assignee.user.first_name?.trim() || '';
    const lastName = conversation.assignee.user.last_name?.trim() || '';
    const email = conversation.assignee.user.email?.trim() || '';

    const fromNames = `${firstName[0] || ''}${lastName[0] || ''}`.toUpperCase();
    if (fromNames) {
      return fromNames;
    }

    return email.slice(0, 2).toUpperCase();
  };

  const isResolved = conversation.resolution_status === ConversationStatusEnum.Resolved;
  const resolutionLabel = isResolved
    ? t('sidebar_menu:menu.conversations.resolved')
    : t('sidebar_menu:menu.conversations.open');
  const badgeDisplay = conversation.badge ? formatConversationBadge(conversation.badge) : null;

  return (
    <TooltipProvider>
      <Link
        className="!no-underline peer"
        to={{ pathname: conversation.id, search: searchParams.toString() }}
        onMouseEnter={() => onHoverChange(true)}
        onMouseLeave={() => onHoverChange(false)}
      >
        <li
          className={classNames(
            'relative min-w-0 rounded-xl p-3 transition-colors hover:bg-accent',
            {
              'bg-accent': isActive,
            },
          )}
        >
          <div className="flex flex-col gap-1 min-w-0">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-1 min-w-0">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="shrink-0 text-foreground-subtle"
                      data-testid="conversation-source"
                      aria-label={sourceLabel}
                    >
                      <ConversationSourceIcon
                        source={conversation.source}
                        assistantSource={conversation.assistant_source}
                      />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{sourceLabel}</TooltipContent>
                </Tooltip>
                <p
                  className={classNames('text-[13px] truncate', {
                    'font-semibold text-foreground': isUnread,
                    'font-medium text-foreground': !isUnread,
                  })}
                >
                  {conversationTitle}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {needsAttention && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="w-[8px] h-[8px] rounded-full bg-blue-500" />
                    </TooltipTrigger>
                    <TooltipContent>{t('needs_attention_tooltip')}</TooltipContent>
                  </Tooltip>
                )}
                <time
                  className={classNames('text-xs', {
                    'font-semibold text-foreground': isUnread,
                    'text-muted-foreground': !isUnread,
                  })}
                >
                  {formatDateToTimeAgo(conversation.last_message_at)}
                </time>
              </div>
            </div>

            <p
              className={classNames('text-xs truncate -mt-px', {
                'font-semibold text-foreground': isUnread,
                'text-muted-foreground': !isUnread,
              })}
            >
              {conversation.last_message_content?.replace(/\*\*(.*?)\*\*/g, '$1')}
            </p>

            <div className="flex items-center gap-1.5 pt-0.5 min-w-0">
              <Badge
                size="xs"
                variant={isUnassigned ? 'error' : 'secondary'}
                className={classNames('min-w-0 max-w-[55%]', {
                  'pl-0': conversation.assignee.type === ConversationAssigneeEnum.HUMAN_OPERATOR,
                  'text-secondary-foreground': !isUnassigned,
                })}
              >
                {conversation.assignee.type === ConversationAssigneeEnum.AI_ASSISTANT && (
                  <Bot className="h-3 w-3 text-foreground-subtle" />
                )}
                {conversation.assignee.type === ConversationAssigneeEnum.HUMAN_OPERATOR && (
                  <Avatar className="h-3.5 w-3.5">
                    <AvatarImage src={conversation.assignee.user.avatar_url || ''} />
                    <AvatarFallback className="bg-accent text-[8px] font-semibold text-muted-foreground">
                      {getAssigneeInitials()}
                    </AvatarFallback>
                  </Avatar>
                )}
                <span className="truncate">{assigneeLabel}</span>
              </Badge>

              {badgeDisplay && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="inline-flex min-w-0 max-w-[40%] items-center">
                      <Badge
                        variant={badgeDisplay.variant}
                        size="xs"
                        className="min-w-0 max-w-full"
                        data-testid="conversation-badge"
                      >
                        <span className="truncate">{badgeDisplay.label}</span>
                      </Badge>
                    </span>
                  </TooltipTrigger>
                  <TooltipContent>{badgeDisplay.label}</TooltipContent>
                </Tooltip>
              )}

              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className="ml-auto inline-flex items-center justify-center"
                    aria-label={resolutionLabel}
                  >
                    {isResolved ? (
                      <CheckCheck className="size-3 text-emerald-600" />
                    ) : (
                      <FolderOpen className="size-3 text-foreground-subtle" />
                    )}
                  </span>
                </TooltipTrigger>
                <TooltipContent>{resolutionLabel}</TooltipContent>
              </Tooltip>
            </div>
          </div>
        </li>
      </Link>
    </TooltipProvider>
  );
};
